import { computed, effectScope, ref, watch } from 'vue';

import type { ComputedRef, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';

import constants from '@/config/constants';
import subcontractorFeaturesDef from '@/services/permissions/subcontractorFeatures';

/**
 * Self-contained "view as subcontractor" feature.
 *
 * Public API:
 *   - useImpersonation()           — state + setters (no permissions awareness)
 *   - applyImpersonationOverlay()  — wraps a usePermissions() result with
 *                                    impersonation-aware overlays
 *   - useImpersonationLifecycle()  — call once in App.vue: returns a remount
 *                                    key for the root <router-view> and wipes
 *                                    the Vue Query cache on impersonation
 *                                    change
 *
 * Removing the feature: delete this file, the selector, the banner, and the
 * `applyImpersonationOverlay()` wrap in usePermissions.ts (one line). No
 * other file needs to change.
 */

// ── Module-scoped state ────────────────────────────────────────────
const _sucoId = ref<string | null>(null);
const _sucoName = ref<string | null>(null);
let _watcherInstalled = false;

export const useImpersonation = () => {
  const isImpersonatingSuco = computed(() => !!_sucoId.value);
  const impersonatedSucoId = computed(() => _sucoId.value);
  const impersonatedSucoName = computed(() => _sucoName.value);

  const setImpersonatedSuco = (payload: {
    id: string | number;
    name?: string | null;
  }) => {
    _sucoId.value = String(payload.id);
    _sucoName.value = payload.name ?? null;
  };

  const clearImpersonatedSuco = () => {
    _sucoId.value = null;
    _sucoName.value = null;
  };

  return {
    isImpersonatingSuco,
    impersonatedSucoId,
    impersonatedSucoName,
    setImpersonatedSuco,
    clearImpersonatedSuco
  };
};

// ── Overlay applied on top of usePermissions() output ──────────────
// We narrow only the few fields we actually read; the rest of T flows
// through unchanged so consumers keep their original types.
type RawPermissionsShape = {
  isAdmin: ComputedRef<boolean>;
  isSubContractor: ComputedRef<boolean>;
  subcontractorId: ComputedRef<string | null | undefined>;
  currentSelectedPath: ComputedRef<string | null | undefined>;
  features: ComputedRef<Record<string, boolean>>;
  depotFeatures: ComputedRef<Record<string, boolean>>;
  depotPath: ComputedRef<string | null>;
  selectedContext: Ref<string>;
  selectedDepotPath: Ref<string>;
};

export const applyImpersonationOverlay = <T extends Record<string, unknown>>(
  raw: T
) => {
  // Internal narrowing — caller is expected to pass the usePermissions shape
  const r = raw as unknown as RawPermissionsShape;
  const impersonation = useImpersonation();

  // Only admins can impersonate, and only inside a selected depot — otherwise
  // the selector would surface in admin/tenant contexts where it has no
  // meaning.
  const canImpersonateSuco = computed(
    () =>
      r.isAdmin.value &&
      r.selectedContext.value === constants.scopeTypes.DEPOT &&
      !!r.selectedDepotPath.value
  );

  // Admin-only UI (e.g. RTT/DGW link chips) should hide while "viewing as
  // suco" so the impersonated view matches what the subcontractor sees.
  // Callers that need the original flag can use `isRealAdmin`.
  const isAdmin = computed(
    () => r.isAdmin.value && !impersonation.isImpersonatingSuco.value
  );

  const isSubContractor = computed(
    () => r.isSubContractor.value || impersonation.isImpersonatingSuco.value
  );

  const subcontractorId = computed(() =>
    impersonation.isImpersonatingSuco.value
      ? impersonation.impersonatedSucoId.value
      : r.subcontractorId.value
  );

  const currentSelectedPath = computed(() => {
    if (
      impersonation.isImpersonatingSuco.value &&
      r.selectedContext.value === constants.scopeTypes.DEPOT &&
      r.depotPath.value
    ) {
      return `${r.depotPath.value}.${impersonation.impersonatedSucoId.value}`;
    }
    return r.currentSelectedPath.value;
  });

  // When impersonating, show what the suco itself would see: every feature
  // key turned off, then suco features turned on. Backend gates data, so
  // feature flags only need to mirror the suco's nav.
  const depotFeatures = computed(() => {
    if (!impersonation.isImpersonatingSuco.value) return r.depotFeatures.value;
    const flagged: Record<string, boolean> = {};
    for (const key of Object.keys(r.depotFeatures.value)) flagged[key] = false;
    for (const { name } of subcontractorFeaturesDef) flagged[name] = true;
    return flagged;
  });

  const features = computed(() => {
    if (
      impersonation.isImpersonatingSuco.value &&
      r.selectedContext.value === constants.scopeTypes.DEPOT
    ) {
      return depotFeatures.value;
    }
    return r.features.value;
  });

  // Guarded setter: refuses to start impersonation when not allowed.
  const setImpersonatedSuco: typeof impersonation.setImpersonatedSuco = (
    payload
  ) => {
    if (!canImpersonateSuco.value) return;
    impersonation.setImpersonatedSuco(payload);
  };

  // Drop impersonation when the depot or context changes (also covers logout,
  // since reset() empties both refs). Installed once, in a *detached* effect
  // scope so it lives for the whole app session rather than being owned by
  // whichever component calls usePermissions() first — that component's
  // unmount (e.g. logout, a hash-only nav with no reload) would otherwise
  // dispose the watcher, which _watcherInstalled would then never reinstall.
  if (!_watcherInstalled) {
    _watcherInstalled = true;
    effectScope(true).run(() => {
      watch([r.selectedDepotPath, r.selectedContext], () => {
        impersonation.clearImpersonatedSuco();
      });
    });
  }

  return {
    ...raw,
    isAdmin,
    isRealAdmin: r.isAdmin,
    isSubContractor,
    isRealSubContractor: r.isSubContractor,
    isImpersonatingSuco: impersonation.isImpersonatingSuco,
    impersonatedSucoId: impersonation.impersonatedSucoId,
    impersonatedSucoName: impersonation.impersonatedSucoName,
    canImpersonateSuco,
    subcontractorId,
    currentSelectedPath,
    features,
    depotFeatures,
    setImpersonatedSuco,
    clearImpersonatedSuco: impersonation.clearImpersonatedSuco
  };
};

// ── Exit helper ────────────────────────────────────────────────────
// Lands the user on the depot overview and does a full page reload. The
// reload is the clean reset: impersonation lives in module state, so a fresh
// document boots back as the real admin with no stale suco-scoped data.
export const useExitImpersonation = () => {
  const { isImpersonatingSuco } = useImpersonation();
  const router = useRouter();
  const route = useRoute();

  return () => {
    // Nothing to reset when already viewing as ourselves — avoid a pointless
    // reload (e.g. clicking "View as yourself" while not impersonating).
    if (!isImpersonatingSuco.value) return;

    const { tenantId, externalDepotId } = route.params;
    if (tenantId && externalDepotId) {
      const { href } = router.resolve({
        name: 'depotLanding',
        params: { tenantId, externalDepotId }
      });
      // Hash-based routing: `href` is hash-only (e.g. '#/depot/...'). Assigning
      // it bare would preserve whatever pathname is currently in the address
      // bar — so if it was already polluted (e.g. /depot/...details), it would
      // stay polluted. Rebuild from origin + BASE_URL to reset the pathname
      // without dropping the base path on preview builds (/previews/pr-N/).
      const target = `${window.location.origin}${import.meta.env.BASE_URL}${href}`;
      window.location.assign(target);
      // Reload only on a same-document hash change; skip it when assign() is
      // already navigating, or the same-tick reload() races and aborts it. Same
      // guard as logoutService.logout — see docs/gotchas.md "Routing".
      if (new URL(target).pathname === window.location.pathname) {
        window.location.reload();
      }
      return;
    }
    // No depot params to rebuild the clean URL from — just hard-reload in place
    // to clear module-scoped state.
    window.location.reload();
  };
};

// ── App-level lifecycle ────────────────────────────────────────────
// Call once in App.vue. Returns a key for the root <router-view>; whenever
// the impersonation toggles, the cache is cleared and the subtree remounts —
// so views refetch against the new identity without any per-call-site work.
export const useImpersonationLifecycle = () => {
  const { impersonatedSucoId } = useImpersonation();
  const queryClient = useQueryClient();

  watch(impersonatedSucoId, () => {
    queryClient.clear();
  });

  const appRemountKey = computed(
    () => `suco-${impersonatedSucoId.value ?? 'self'}`
  );

  return { appRemountKey };
};
